// Sugalarm — email + password form. Toggles between sign-in and register
// via a single bottom-of-card link. Cycle 152: the alternate methods
// (Google / Apple / passkey) render beneath the divider when available.

import { useState } from "react";

import type { AuthResponse } from "../../api/auth";
import { Button } from "../../components/ui/Button";
import { Field, inputClass } from "../../components/ui/Field";
import { useFrameVariant } from "../../hooks/useFrameVariant";
import { AuthMethods } from "./AuthMethods";

export type SignInMode = "signin" | "register";

interface Props {
  mode: SignInMode;
  /** Cycle 190: `remember` is supplied only on the web variant (the
   *  "Remember me" checkbox); mobile passes `undefined`. */
  onSubmit: (
    email: string,
    password: string,
    displayName?: string,
    remember?: boolean,
  ) => Promise<void>;
  onSwitchMode: (next: SignInMode) => void;
  /** Cycle 190: route to the self-serve /forgot-password page. */
  onForgotPassword: () => void;
  /** Cycle 152: an alternate method (Google/Apple/passkey) succeeded. */
  onAltSuccess: (data: AuthResponse) => void;
  onAltError: (message: string) => void;
  error: string | null;
  busy: boolean;
}

export function SignInForm({
  mode,
  onSubmit,
  onSwitchMode,
  onForgotPassword,
  onAltSuccess,
  onAltError,
  error,
  busy,
}: Props) {
  const isWeb = useFrameVariant() === "web";
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [name, setName] = useState("");
  // Cycle 190: "Remember me" — web-only, default ON (existing web users'
  // persistence is unchanged). Drives localStorage (on) vs sessionStorage
  // (off) for the JWT pair. Not rendered on mobile/kiosk.
  const [remember, setRemember] = useState(true);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !pw || (mode === "register" && !name)) return;
    await onSubmit(email, pw, name, isWeb ? remember : undefined);
  };

  return (
    <form
      onSubmit={submit}
      style={{
        position: "relative",
        width: 380,
        background: "var(--bg-elev)",
        border: "0.5px solid var(--line-strong)",
        borderRadius: 16,
        padding: 22,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div>
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: "-0.02em",
          }}
        >
          {mode === "signin" ? "Sign in to Sugar Alarm" : "Create your Sugar Alarm account"}
        </h1>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--ink-2)" }}>
          {mode === "signin"
            ? "Use your email and password."
            : "Email, password, and a display name. Takes a few seconds."}
        </p>
      </div>

      {mode === "register" && (
        <Field label="Display name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Display name"
            autoFocus
            className={inputClass}
          />
        </Field>
      )}

      <Field label="Email">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@home.com"
          autoFocus={mode === "signin"}
          className={inputClass}
        />
      </Field>
      <Field label="Password">
        <input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="••••••••••"
          className={inputClass}
        />
      </Field>

      {/* Cycle 190: sign-in row — "Remember me" (web only) on the left,
          "Forgot password?" (web + mobile) on the right. Both hidden in
          register mode. */}
      {mode === "signin" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            fontSize: "var(--type-floor)",
            marginTop: -2,
          }}
        >
          {isWeb ? (
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: "var(--ink-2)",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                style={{ accentColor: "var(--accent)", cursor: "pointer" }}
              />
              Remember me
            </label>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={onForgotPassword}
            style={{
              appearance: "none",
              border: 0,
              background: "transparent",
              color: "var(--accent)",
              fontWeight: 600,
              fontSize: "var(--type-floor)",
              cursor: "pointer",
              padding: 0,
            }}
          >
            Forgot password?
          </button>
        </div>
      )}

      {error && (
        <div style={{ color: "var(--zone-urgent)", fontSize: "var(--type-floor)", fontWeight: 500 }}>
          {error}
        </div>
      )}

      <Button
        type="submit"
        full
        disabled={busy || !email || !pw || (mode === "register" && !name)}
      >
        {busy
          ? "Working…"
          : mode === "signin"
            ? "Sign in"
            : "Create account"}
      </Button>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: "var(--ink-3)",
          fontSize: "var(--type-floor)",
        }}
      >
        <span style={{ flex: 1, height: 1, background: "var(--line)" }} />
        OR
        <span style={{ flex: 1, height: 1, background: "var(--line)" }} />
      </div>

      {/* Cycle 152: Google / Apple / passkey — renders only the methods
          the server reports configured (and the browser supports);
          renders nothing on the kiosk variant. */}
      <AuthMethods onSuccess={onAltSuccess} onError={onAltError} />

      <div
        style={{
          fontSize: "var(--type-floor)",
          color: "var(--ink-3)",
          textAlign: "center",
          marginTop: 4,
        }}
      >
        {mode === "signin" ? (
          <>
            New here?{" "}
            <button
              type="button"
              onClick={() => onSwitchMode("register")}
              style={{
                appearance: "none",
                border: 0,
                background: "transparent",
                color: "var(--accent)",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Create an account
            </button>
          </>
        ) : (
          <>
            Already have an account?{" "}
            <button
              type="button"
              onClick={() => onSwitchMode("signin")}
              style={{
                appearance: "none",
                border: 0,
                background: "transparent",
                color: "var(--accent)",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Sign in
            </button>
          </>
        )}
      </div>
    </form>
  );
}
